import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../core/services/api/api";
import {LookupAnalyticsLogsDataGridColumnNames} from "../../../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import DateUtils from "../../../../../../../core/services/date-utils";
import {DefaultPaginationInfo} from "../../../../../../../core/constants/enums";
import useSearch from "../../../../../../hooks/use-search";
import IconButton from "../../../../../../components/base/button/icon_button";
import {Link} from "react-router-dom";
import {stringifyUrl} from "query-string";
import {routes} from "../../../../../../../core/models/static/routes/app-routes";
import {OpenInNew} from "@mui/icons-material";
import LookupAppliedFiltersPopover from "../../../../../../components/app-specific/draggable-popover/lookup-applied-filters-popover";
import ProductInformationPopover from "../../../../../../components/app-specific/draggable-popover/product-information-popover";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {LookupAnalyticsDateOptionRanges} from "../../../../../../components/search-sections/lookup-analytics/dashboard";
import {Box, Typography} from "@mui/material";

const dataGridSaveConfig = {
    name: 'LookupPartInformationVisitLogsDataGrid',
    version: 2,
}

const LookupProductInformationViewVisitLogsSection = ({
                                                          customerId,
                                                          partId,
                                                          industryId,
                                                          id,
                                                          className,
                                                          removeSection,
                                                          addSection,
                                                          shown,
                                                      }) => {
    const [data, setData] = useState([]);
    const operatorAccessLevels = useAccessLevels();
    const isMounted = useIsMounted();

    /**
     * When the component is mounted, if the user has access to view nexus search logs, add this section to the view.
     */
    useEffect(() => {
        if (operatorAccessLevels.packages.lookup.nexusSearch) {
            addSection(id);
        }
    }, [id, addSection, operatorAccessLevels.packages.lookup.nexusSearch])


    /**
     * Memo version of columns entities used in data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                name: LookupAnalyticsLogsDataGridColumnNames.navigate,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                width: 0,
                sortable: false,
                resizable: false,
                pinned: true,
            },
            {
                title: "Filters Count",
                name: LookupAnalyticsLogsDataGridColumnNames.filters,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
            },
            {
                title: "Keyword",
                name: LookupAnalyticsLogsDataGridColumnNames.keyword,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "User",
                name: LookupAnalyticsLogsDataGridColumnNames.user,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Customer",
                name: LookupAnalyticsLogsDataGridColumnNames.customer,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Operator",
                name: LookupAnalyticsLogsDataGridColumnNames.operator,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Clicked Part",
                name: LookupAnalyticsLogsDataGridColumnNames.clickedPart,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Result Count",
                name: LookupAnalyticsLogsDataGridColumnNames.resultCount,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: "Submit Date Time",
                name: LookupAnalyticsLogsDataGridColumnNames.submitDateTime,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.dateTime,
                width: 120,
            },
            {
                title: "Lost Search Description",
                name: LookupAnalyticsLogsDataGridColumnNames.lostSearchDescription,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 160,
                sortable: false,
            },
            {
                title: "Reviewed",
                name: LookupAnalyticsLogsDataGridColumnNames.hasBeenReviewed,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
            },
            {
                title: "Review Note",
                name: LookupAnalyticsLogsDataGridColumnNames.reviewNote,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
            },
            {
                title: "Repeat Count",
                name: LookupAnalyticsLogsDataGridColumnNames.repeatCount,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
        ], [])

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Gets the visit logs of the user from the server.
     */
    const fetchEntries = useCallback(async (args, forExport) => {
        const forApi = {
            sorts: args.orderBy?.field ? [{
                property: args.orderBy?.field,
                isDesc: args.orderBy?.descending,
            }] : [],
            pagination: args.paginationInfo,
            industryId: industryId,
            customerId: customerId,
            partId: partId,
        }
        const defaultPeriod = LookupAnalyticsDateOptionRanges.last30Days();
        defaultPeriod.startDate = DateUtils.UTCToServer(defaultPeriod.startDate).format(DateUtils.withoutOffsetFormat);
        defaultPeriod.endDate = DateUtils.UTCToServer(defaultPeriod.endDate).format(DateUtils.withoutOffsetFormat);

        forApi.period = {
            ...forApi.period,
            startDateTime: forApi.period?.startDateTime ?? defaultPeriod.startDate,
            endDateTime: forApi.period?.endDateTime ?? defaultPeriod.endDate,
        }

        const response = await Api.getNexusSearchReportNexusLogs(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setData(response.data.items);
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response?.data?.totalCount ?? 0
            }));
        }
        if (args.paginationInfo.currentPage === 1 && (!response?.resultFlag || response.data.items.length === 0) && !forExport) {
            removeSection(id);
        }
        if (response?.resultFlag && forExport) {
            return response;
        }
    }, [industryId, customerId, partId, isMounted, removeSection, id])

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize,
        filters: {},
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
    }), [initialPaginationInfo, dataGridSortBy]);

    const searchData = useSearch(search, initialSearchDataSettings, false)

    /**
     * Memo version of table row records used in data grid
     */
    const rows = useMemo(() => {
        return data?.map(log => ({
            key: log.id,
            data: log,
            cells: {
                [LookupAnalyticsLogsDataGridColumnNames.navigate]:
                    operatorAccessLevels.packages.lookup.nexusSearch &&
                    <IconButton
                        color={'secondary'}
                        component={Link}
                        to={stringifyUrl({
                            url: routes.lookup.search,
                            query: {
                                searchToken: log.id,
                                readonly: true,
                            },
                        })}
                    >
                        <OpenInNew className='icon normal'/>
                    </IconButton>,
                [LookupAnalyticsLogsDataGridColumnNames.filters]:
                    <LookupAppliedFiltersPopover
                        filters={log.filters}
                        title={`Searched By ${log.userName ?? log.customerName ?? log.partnerStaffName ?? ""} at ${DateUtils.serverToLocal(log.submitDateTime).format(DateUtils.dateFormatAlternative)}`}
                    >
                        {log.filters.length ?? 0}
                    </LookupAppliedFiltersPopover>,
                [LookupAnalyticsLogsDataGridColumnNames.keyword]: log.keyword,
                [LookupAnalyticsLogsDataGridColumnNames.user]: log.userName,
                [LookupAnalyticsLogsDataGridColumnNames.customer]: log.customerName,
                [LookupAnalyticsLogsDataGridColumnNames.operator]: log.partnerStaffName,
                [LookupAnalyticsLogsDataGridColumnNames.clickedPart]: log.partNumber
                    ? <ProductInformationPopover partNo={log.partNumber}>{log.partNumber ?? ""}</ProductInformationPopover>
                    : log.partNumber,
                [LookupAnalyticsLogsDataGridColumnNames.resultCount]: log.foundedItem,
                [LookupAnalyticsLogsDataGridColumnNames.submitDateTime]: log.submitDateTime,
                [LookupAnalyticsLogsDataGridColumnNames.lostSearchDescription]: log.lostSearchDescription,
                [LookupAnalyticsLogsDataGridColumnNames.repeatCount]: log.repeatCount,
            }
        })) ?? []
    }, [operatorAccessLevels.packages.lookup.nexusSearch, data])

    return (
        <Box id={id} className={`${className} product-visit-logs-container`} sx={{display: shown ? 'flex' : 'none', flexDirection: 'column', gap: 2, width: '100%'}}>
            <Typography variant="body1" fontWeight={600}>
                Product Visit Logs
            </Typography>
            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{columns: columns}}
                columns={dataGridColumns}
                rows={rows}
                hideFooterActions
                hideExporting
                classNames={{
                    toolbar: 'visit-logs-toolbar'
                }}
                state={{
                    sortBy: searchData.orderBy,
                    pagination: searchData.paginationInfo,
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
            />
            <div className={'w-100 my-2'}/>
        </Box>
    );

}

export default LookupProductInformationViewVisitLogsSection
